import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Package, Loader2, Filter, Plus, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AssetType = 'model' | 'dataset' | 'pipeline' | 'application';
type AssetStatus = 'production' | 'development' | 'retired';

interface AIAsset {
  id: string;
  name: string;
  description: string;
  asset_type: AssetType;
  owner: string;
  status: AssetStatus;
  department: string;
  updated_at: string;
}

interface AssetsResponse {
  assets: AIAsset[];
  total: number;
  production: number;
  development: number;
  retired: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ASSET_TYPES: AssetType[] = ['model', 'dataset', 'pipeline', 'application'];
const ASSET_STATUSES: { value: AssetStatus; label: string }[] = [
  { value: 'production', label: 'Production' },
  { value: 'development', label: 'Development' },
  { value: 'retired', label: 'Retired' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function typeBadge(type: AssetType) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (type) {
    case 'model':
      return <span className={`${base} bg-purple-500/15 text-purple-500`}>Model</span>;
    case 'dataset':
      return <span className={`${base} bg-blue-500/15 text-blue-500`}>Dataset</span>;
    case 'pipeline':
      return <span className={`${base} bg-cyan-500/15 text-cyan-500`}>Pipeline</span>;
    case 'application':
      return <span className={`${base} bg-emerald-500/15 text-emerald-500`}>Application</span>;
  }
}

function statusBadge(status: AssetStatus) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'production':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Production</span>;
    case 'development':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>Development</span>;
    case 'retired':
      return <span className={`${base} bg-muted text-muted-foreground`}>Retired</span>;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function AssetsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assetType, setAssetType] = useState<AssetType>('model');
  const [owner, setOwner] = useState('');
  const [status, setStatus] = useState<AssetStatus>('development');
  const [department, setDepartment] = useState('');

  const assetsQuery = useQuery<AssetsResponse>({
    queryKey: ['governance', 'assets'],
    queryFn: () => api<AssetsResponse>('governance/models', { query: { scope: 'assets' } }),
  });

  const createMutation = useMutation({
    mutationFn: (body: Omit<AIAsset, 'id' | 'updated_at'>) =>
      api<AIAsset>('governance/models', { body: { ...body, scope: 'assets' } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governance', 'assets'] });
      resetForm();
    },
  });

  function resetForm() {
    setShowForm(false);
    setName('');
    setDescription('');
    setAssetType('model');
    setOwner('');
    setStatus('development');
    setDepartment('');
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !owner.trim()) return;
    createMutation.mutate({
      name: name.trim(),
      description: description.trim(),
      asset_type: assetType,
      owner: owner.trim(),
      status,
      department: department.trim(),
    });
  }

  const assets = assetsQuery.data?.assets ?? [];
  const filtered = filterType === 'all' ? assets : assets.filter((a) => a.asset_type === filterType);
  const stats = assetsQuery.data;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Package className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">AI Asset Registry</h1>
            <p className="text-muted-foreground mt-1">Catalog and manage all AI assets including models, datasets, pipelines, and applications.</p>
          </div>
        </div>
        {!showForm && (
          <button type="button" onClick={() => setShowForm(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 flex-shrink-0">
            <Plus className="h-4 w-4" />
            Create Asset
          </button>
        )}
      </div>

      {/* Stats */}
      {assetsQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Total Assets</p>
            <p className="text-2xl font-bold text-foreground">{stats?.total ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Production</p>
            <p className="text-2xl font-bold text-green-500">{stats?.production ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Development</p>
            <p className="text-2xl font-bold text-yellow-500">{stats?.development ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Retired</p>
            <p className="text-2xl font-bold text-muted-foreground">{stats?.retired ?? 0}</p>
          </div>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-border bg-card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Create New Asset</h3>
            <button type="button" onClick={resetForm} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Asset Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Customer Sentiment Model" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Asset Type</label>
              <select value={assetType} onChange={(e) => setAssetType(e.target.value as AssetType)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                {ASSET_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Owner</label>
              <input value={owner} onChange={(e) => setOwner(e.target.value)} required placeholder="e.g. John Smith" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Department</label>
              <input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Engineering" className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as AssetStatus)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
                {ASSET_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Brief description of the asset..." className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-y" />
          </div>

          {createMutation.isError && (
            <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create asset.'}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button type="submit" disabled={createMutation.isPending || !name.trim() || !owner.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {createMutation.isPending ? 'Creating...' : 'Create Asset'}
            </button>
            <button type="button" onClick={resetForm} className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
          <option value="all">All Types</option>
          {ASSET_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
      </div>

      {/* Asset table */}
      {assetsQuery.isLoading && (
        <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Loading assets...</span>
        </div>
      )}

      {assetsQuery.isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-6 text-center">
          <p className="text-sm text-red-500">
            Failed to load assets.{' '}
            <button type="button" onClick={() => assetsQuery.refetch()} className="underline hover:no-underline">Retry</button>
          </p>
        </div>
      )}

      {assetsQuery.isSuccess && filtered.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
          {filterType === 'all' ? 'No assets registered yet. Click "Create Asset" to get started.' : `No assets found for type "${filterType}".`}
        </div>
      )}

      {assetsQuery.isSuccess && filtered.length > 0 && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-4 px-4 py-2 bg-muted/50 border-b border-border text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <span className="flex-1">Name</span>
            <span className="w-24">Type</span>
            <span className="w-28">Owner</span>
            <span className="w-24">Status</span>
            <span className="w-28">Department</span>
            <span className="w-28">Last Updated</span>
          </div>
          {filtered.map((asset) => (
            <div key={asset.id} className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 hover:bg-accent/50">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{asset.name}</p>
                {asset.description && <p className="text-xs text-muted-foreground truncate">{asset.description}</p>}
              </div>
              <span className="w-24">{typeBadge(asset.asset_type)}</span>
              <span className="w-28 text-sm text-foreground truncate">{asset.owner}</span>
              <span className="w-24">{statusBadge(asset.status)}</span>
              <span className="w-28 text-sm text-muted-foreground truncate">{asset.department || '--'}</span>
              <span className="w-28 text-sm text-muted-foreground">{formatDate(asset.updated_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
